import { useEffect, useState } from 'react';
import { classNames } from '@/lib/utils';
import { getCachedIcon, getIcon, type IconName } from '@/utils/iconRegistry';

export interface IconProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  name: IconName;
  fallback?: React.ReactNode;
}

export function Icon({ name, className, alt, fallback, ...props }: IconProps) {
  // Initialize with cached icon if available to avoid flicker
  const [src, setSrc] = useState<string>(() => getCachedIcon(name));
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        // Always call getIcon to ensure validation/loading, even if we had a cache hit initially
        // (getIcon handles returning cached value efficiently)
        const url = await getIcon(name);

        if (mounted) {
          if (url) {
            setSrc(url);
            setError(false);
          } else {
            setError(true);
          }
        }
      } catch (_e) {
        if (mounted) setError(true);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [name]);

  if (error) {
    if (fallback) return <>{fallback}</>;
    return null;
  }

  if (!src) {
    if (fallback) return <>{fallback}</>;
    // Render a placeholder with same class to maintain layout
    return <div className={classNames('inline-block', className)} role="presentation" aria-hidden="true" />;
  }

  return (
    <img src={src} alt={alt || name} className={classNames('inline-block object-contain', className)} {...props} />
  );
}
